import { useEffect, useState, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Plus, X, Trash2, Search } from "lucide-react";

export const GrupoEquivalenciaDetalle = ({ grupo, onBack, onChanged }) => {
  const [nombre, setNombre] = useState(grupo.nombre);
  const [repuestos, setRepuestos] = useState([]);
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);

  const cargar = useCallback(async () => {
    const r = await api.get(`/grupos-equivalencia/${grupo.id}/repuestos`);
    setRepuestos(r.data);
  }, [grupo.id]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!q.trim()) { setResultados([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/repuestos", { params: { q } });
        setResultados(r.data.filter((x) => x.grupo_equivalencia_id !== grupo.id).slice(0, 8));
      } catch { setResultados([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, grupo.id]);

  const renombrar = async () => {
    try { await api.patch(`/grupos-equivalencia/${grupo.id}`, { nombre }); onChanged(); toast.success("Grupo renombrado"); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const eliminarGrupo = async () => {
    if (!window.confirm("¿Eliminar este grupo? Los repuestos que lo usan quedarán sin grupo.")) return;
    try { await api.delete(`/grupos-equivalencia/${grupo.id}`); onChanged(); toast.success("Grupo eliminado"); onBack(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const asignar = async (rep) => {
    try {
      await api.patch(`/repuestos/${rep.id}`, { grupo_equivalencia_id: grupo.id });
      setQ(""); setResultados([]); cargar(); onChanged();
      toast.success(`${rep.codigo_parte} agregado al grupo`);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const quitar = async (rep) => {
    try {
      await api.patch(`/repuestos/${rep.id}`, { grupo_equivalencia_id: null });
      cargar(); onChanged();
      toast.success(`${rep.codigo_parte} quitado del grupo`);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  return (
    <div className="space-y-3" data-testid="grupo-eq-detalle">
      <div className="flex items-center gap-2">
        <Button data-testid="volver-grupos-btn" variant="ghost" size="icon" className="h-7 w-7" onClick={onBack}><ArrowLeft size={15} /></Button>
        <Input data-testid="grupo-detalle-nombre-input" className="h-8" value={nombre} onChange={(e) => setNombre(e.target.value)} />
        <Button data-testid="renombrar-grupo-btn" size="sm" onClick={renombrar} disabled={!nombre.trim() || nombre === grupo.nombre}>Renombrar</Button>
        <Button data-testid="eliminar-grupo-detalle-btn" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={eliminarGrupo}><Trash2 size={15} /></Button>
      </div>

      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-1">Repuestos del grupo ({repuestos.length})</p>
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {repuestos.length === 0 && <p className="text-sm text-muted-foreground">Este grupo aún no tiene repuestos.</p>}
          {repuestos.map((r) => (
            <div key={r.id} data-testid={`grupo-repuesto-${r.codigo_parte}`} className="flex items-center justify-between border border-border rounded-sm px-3 py-1.5 text-sm">
              <span><span className="font-mono text-xs font-semibold mr-2">{r.codigo_parte}</span>{r.nombre}</span>
              <Button data-testid={`quitar-del-grupo-${r.codigo_parte}`} variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => quitar(r)} title="Quitar del grupo"><X size={13} /></Button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-1">Agregar repuesto al grupo</p>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="buscar-repuesto-grupo-input" className="pl-8 h-8" placeholder="Buscar por código o nombre..."
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {resultados.length > 0 && (
          <div className="space-y-1 max-h-36 overflow-y-auto mt-1 border border-border rounded-sm p-1">
            {resultados.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-2 py-1 text-sm hover:bg-secondary rounded-sm">
                <span>
                  <span className="font-mono text-xs font-semibold mr-2">{r.codigo_parte}</span>{r.nombre}
                  {r.grupo_equivalencia && <span className="text-xs text-amber-700 ml-2">(en grupo: {r.grupo_equivalencia.nombre})</span>}
                </span>
                <Button data-testid={`agregar-al-grupo-${r.codigo_parte}`} variant="ghost" size="icon" className="h-6 w-6 text-green-700" onClick={() => asignar(r)} title="Agregar al grupo"><Plus size={14} /></Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
